import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRightLeft, ChevronsUpDown, History, LayoutGrid, Settings, Users } from 'lucide-react';
import { Fragment } from 'react';

import type { SiteBody } from '@/shared/api/generated/model';
import { sitesList } from '@/shared/api/generated/sites/sites';
import { t } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Tooltip } from '@/shared/ui/Tooltip';

import type { SiteScope } from '../site/useSiteScope';

import styles from './SiteSwitcher.module.scss';

/**
 * Текущий сайт и переход на другие — шапка колонки внутри сайта.
 *
 * Заменяет знак Faceta, как переключатель пространства во flow-do: человек
 * работает с сайтом, а не с продуктом, и его имя должно быть первым, что
 * он видит. Тенант в списке появляется, только когда тенантов больше
 * одного: при одном это шум.
 *
 * Другой сайт открывается на Production: у него может не быть Development,
 * а «не найдено» вместо сайта — плохой ответ на выбор из списка.
 *
 * Сверху — то, что относится к открытому сайту и нужно реже содержимого:
 * настройки, команда, журнал, перенос (по просьбе Давида — не в сайдбаре).
 */
export function SiteSwitcher({
  scope,
  compact,
  onNavigate,
}: {
  scope: SiteScope;
  compact: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const navigate = useNavigate();

  const sites = useQuery({
    queryKey: ['switcher', 'sites'],
    queryFn: async () => {
      const response = await sitesList({ per_page: 100, sort: 'name', order: 'asc' });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос сайтов');
      }

      return response.data.items ?? [];
    },
  });

  if (scope.site === undefined) {
    return <Skeleton height={compact ? 28 : 36} width={compact ? 28 : '100%'} />;
  }

  const name = scope.site.name;
  const others = (sites.data ?? []).filter((site) => site.id !== scope.siteId);
  const groups = byTenant(others);
  const base = `/sites/${scope.siteId}/${scope.env}` as const;
  const open = (to: string) => {
    onNavigate?.();
    void navigate({ to });
  };

  const own = [
    scope.can('site.shape') && {
      icon: <Settings />,
      label: t.nav.settings,
      to: `${base}/settings`,
    },
    scope.can('people.manage') && { icon: <Users />, label: t.nav.team, to: `${base}/team` },
    scope.can('people.manage') && {
      icon: <History />,
      label: t.nav.activity,
      to: `${base}/activity`,
    },
    scope.can('site.shape') && {
      icon: <ArrowRightLeft />,
      label: t.nav.transfer,
      to: `${base}/transfer`,
    },
  ].filter((item) => item !== false);

  const trigger = (
    <button
      type="button"
      className={cx(styles['switcher'], compact && styles['switcher--compact'])}
      aria-label={compact ? name : undefined}
    >
      <span className={styles['switcher__mark']} aria-hidden="true">
        {name.charAt(0).toUpperCase()}
      </span>
      {!compact && (
        <>
          <span className={styles['switcher__name']}>{name}</span>
          <ChevronsUpDown className={styles['switcher__chevron']} />
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <Tooltip content={name} side="right">
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6}>
        <DropdownMenuLabel>{name}</DropdownMenuLabel>
        {own.map((item) => (
          <DropdownMenuItem
            key={item.to}
            icon={item.icon}
            onSelect={() => {
              open(item.to);
            }}
          >
            {item.label}
          </DropdownMenuItem>
        ))}
        {groups.length > 0 && <DropdownMenuSeparator />}
        {groups.length > 0 && groups.length === 1 && (
          <DropdownMenuLabel>{t.nav.otherSites}</DropdownMenuLabel>
        )}
        {groups.map((group) => (
          <Fragment key={group.tenant}>
            {groups.length > 1 && <DropdownMenuLabel>{group.tenant}</DropdownMenuLabel>}
            {group.sites.map((site) => (
              <DropdownMenuItem
                key={site.id}
                icon={<span className={styles['dot']} />}
                onSelect={() => {
                  onNavigate?.();
                  void navigate({
                    to: '/sites/$siteId/$env',
                    params: { siteId: site.id, env: 'prod' },
                  });
                }}
              >
                {site.name}
              </DropdownMenuItem>
            ))}
          </Fragment>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LayoutGrid />}
          onSelect={() => {
            open('/sites');
          }}
        >
          {t.nav.allSites}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function byTenant(list: SiteBody[]): { tenant: string; sites: SiteBody[] }[] {
  const groups = new Map<string, SiteBody[]>();

  for (const site of list) {
    const group = groups.get(site.tenant_name) ?? [];
    group.push(site);
    groups.set(site.tenant_name, group);
  }

  return [...groups].map(([tenant, sites]) => ({ tenant, sites }));
}
